import {useEffect, useState} from 'react';
import pluralize from 'pluralize';

import {AnimatedText, Icon, Link, NumberTicker, Text} from '#component/base';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {Transition} from '#component/util';
import {sprinkles} from '#style/sprinkles';

import {StepProps} from '../../StorySteps.types';
import {animTextCentered, animTextHighlight} from '../StorySteps.css';

import * as css from './Step3.css';

export const Step3 = ({
  afterEnter,
  deltaAge,
  formattedBioAge,
  formattedChronologicalAge,
  formattedDeltaAge,
  isYounger = true,
  show,
  labResultReportUrl,
}: StepProps) => {
  const [animateAge, setAnimateAge] = useState(false);
  const {track} = useAnalytics();
  const {years, months} = formattedBioAge || {};
  const {years: deltaYears, months: deltaMonths} = formattedDeltaAge || {};
  const {years: chronoYears, months: chronoMonths} = formattedChronologicalAge || {};
  const hasMonths = months > 0;
  const renderedAdjective = isYounger ? 'younger than' : 'older than';
  const renderedDeltaMonths =
    deltaMonths > 0 ? ` and ${pluralize('month', deltaMonths, true)} ` : ' ';
  const renderedChronoMonths =
    chronoMonths > 0 ? ` and ${pluralize('month', chronoMonths, true)} ` : '';
  const renderedChronoAge = renderedChronoMonths
    ? `${pluralize('year', chronoYears, true)}${renderedChronoMonths}`
    : chronoYears;
  const isEqual = deltaAge === 0;

  useEffect(() => {
    const ageTimeout = setTimeout(() => {
      setAnimateAge(!!show);
    }, 500);

    return () => {
      clearTimeout(ageTimeout);
    };
  }, [show]);

  return (
    <Transition className={sprinkles({position: 'absolute', inset: 0})} show={show}>
      <div className={css.age}>
        <NumberTicker className={css.ageYears} color="white">
          {animateAge ? years : chronoYears}
        </NumberTicker>

        <AnimatedText color="white" fontWeight="semiBold" variant="bodyLarge">
          {hasMonths
            ? `${pluralize('year', years)} and ${pluralize('month', months, true)}`
            : `${pluralize('year', years)}`}
        </AnimatedText>
      </div>

      <div className={animTextCentered}>
        <p>
          <AnimatedText as="span" color="white" variant="heading3">
            Your TallyAge™ is
          </AnimatedText>

          <AnimatedText as="span" className={animTextHighlight} delay={20} variant="heading3">
            {isEqual
              ? 'exactly the same'
              : `${pluralize('year', deltaYears, true)}${renderedDeltaMonths}${renderedAdjective}`}
          </AnimatedText>

          <AnimatedText
            afterEnter={afterEnter}
            as="span"
            delay={140}
            color="white"
            variant="heading3">
            {isEqual
              ? 'as your chronological age'
              : `your chronological age of ${renderedChronoAge}`}
          </AnimatedText>
        </p>
        <div className={css.download}>
          <Transition.Child className={css.downloadTransition}>
            <Link
              href={labResultReportUrl}
              className={css.downloadLink}
              onClick={() =>
                track<UserActionEvent>({
                  evtName: EventType.UserAction,
                  actionName: 'Lab Results Report Downloaded',
                }).catch()
              }>
              <Text color="white" variant="caption">
                Download Report
              </Text>
              <Icon name="DownloadSimple" color="white" />
            </Link>
          </Transition.Child>
        </div>
      </div>
    </Transition>
  );
};
